import { storeAudioBlob, probeAudioDuration } from '../media';
import { estimateWordTimings } from '../captionTiming';
import type { MediaAsset, OpenAiTtsModel, OpenAiVoice, TtsVoiceOption, WordTiming } from '@/types';

export type SynthesizeParams =
  | {
      provider: 'google';
      text: string;
      languageCode: string;
      voiceName: string;
      voiceGender: 'FEMALE' | 'MALE' | 'NEUTRAL';
      speakingRate: number;
      pitch: number;
    }
  | {
      provider: 'openai';
      text: string;
      openaiVoice: OpenAiVoice;
      openaiModel: OpenAiTtsModel;
      speakingRate: number;
    };

export interface SynthesizeResult {
  asset: MediaAsset;
  durationSeconds: number;
  wordTimings: WordTiming[];
  timingSource: 'marks' | 'estimated';
}

export class TtsApiError extends Error {}

/**
 * Calls the secure Cloudflare Function for the selected provider (Google
 * Cloud TTS or OpenAI TTS-1) and stores the resulting audio locally. Both
 * providers return the same `{ audioContentBase64, audioEncoding }` shape
 * from their respective Functions, so everything past the fetch call is
 * provider-agnostic — including caption timing, which is always estimated
 * client-side from the script text and resulting audio duration.
 */
export async function synthesizeVoiceover(params: SynthesizeParams): Promise<SynthesizeResult> {
  const endpoint = params.provider === 'google' ? '/api/tts' : '/api/openai-tts';
  const requestBody =
    params.provider === 'google'
      ? {
          text: params.text,
          languageCode: params.languageCode,
          voiceName: params.voiceName,
          ssmlGender: params.voiceGender,
          speakingRate: params.speakingRate,
          pitch: params.pitch,
          audioEncoding: 'MP3',
        }
      : {
          text: params.text,
          voice: params.openaiVoice,
          model: params.openaiModel,
          speed: params.speakingRate,
        };

  const response = await fetch(endpoint, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(requestBody),
  });

  if (!response.ok) {
    const body = await safeJson(response);
    throw new TtsApiError(body?.error ?? 'Voiceover generation failed. Please try again.');
  }

  const body = (await response.json()) as {
    audioContentBase64: string;
    wordTimepoints?: Array<{ word: string; timeSeconds: number }> | null;
  };

  const blob = base64ToBlob(body.audioContentBase64, 'audio/mpeg');
  const asset = await storeAudioBlob(blob, `voiceover-${Date.now()}.mp3`);
  const durationSeconds = asset.durationSeconds ?? (await probeAudioDuration(blob));

  // Google's route may return word timepoints in principle (see functions/api/_lib/googleTts.ts
  // for why it currently never does); OpenAI's route never includes them. Either way this falls
  // back to the estimation model, which is the normal path today for both providers.
  if (body.wordTimepoints && body.wordTimepoints.length > 0) {
    return {
      asset,
      durationSeconds,
      wordTimings: timepointsToTimings(body.wordTimepoints, durationSeconds),
      timingSource: 'marks',
    };
  }

  return {
    asset,
    durationSeconds,
    wordTimings: estimateWordTimings(params.text, durationSeconds, params.speakingRate),
    timingSource: 'estimated',
  };
}

export async function fetchVoiceOptions(languageCode?: string): Promise<TtsVoiceOption[]> {
  const url = languageCode ? `/api/voices?languageCode=${encodeURIComponent(languageCode)}` : '/api/voices';
  const response = await fetch(url);
  if (!response.ok) {
    const body = await safeJson(response);
    throw new TtsApiError(body?.error ?? 'Could not load available voices.');
  }
  const data = (await response.json()) as { voices?: Array<{ name: string; languageCodes: string[]; ssmlGender: string; naturalSampleRateHertz: number }> };
  return (data.voices ?? []).flatMap((v) =>
    v.languageCodes.map((lc) => ({
      name: v.name,
      languageCode: lc,
      ssmlGender: (v.ssmlGender as TtsVoiceOption['ssmlGender']) ?? 'NEUTRAL',
      naturalSampleRateHertz: v.naturalSampleRateHertz,
    }))
  );
}

function timepointsToTimings(timepoints: Array<{ word: string; timeSeconds: number }>, totalDuration: number): WordTiming[] {
  return timepoints.map((tp, i) => ({
    word: tp.word,
    startSeconds: tp.timeSeconds,
    endSeconds: timepoints[i + 1]?.timeSeconds ?? totalDuration,
  }));
}

function base64ToBlob(base64: string, mimeType: string): Blob {
  const byteChars = atob(base64);
  const byteNumbers = new Array(byteChars.length);
  for (let i = 0; i < byteChars.length; i++) byteNumbers[i] = byteChars.charCodeAt(i);
  return new Blob([new Uint8Array(byteNumbers)], { type: mimeType });
}

async function safeJson(response: Response): Promise<{ error?: string } | null> {
  try {
    return await response.json();
  } catch {
    return null;
  }
}
